import { z } from "zod"

export const GALLERY_CANVAS_VERSION = 1 as const
export const GALLERY_CANVAS_RENDERER_VERSION = "gallery-canvas-react-v2" as const
export const MAX_GALLERY_CANVAS_LAYERS = 150
export const MAX_GALLERY_CANVAS_ASSETS = 64
export const MAX_GALLERY_CANVAS_DOCUMENT_BYTES = 1_000_000
export const MAX_GALLERY_CANVAS_ASSET_BYTES = 32 * 1024 * 1024
export const MAX_GALLERY_CANVAS_TOTAL_ASSET_BYTES = 128 * 1024 * 1024

export const GALLERY_CANVAS_LAYER_KIND = {
  background: "background",
  product: "product",
  text: "text",
  icon: "icon",
  shape: "shape",
  panel: "panel",
  group: "group",
} as const

export const GALLERY_CANVAS_ASSET_ROLE = {
  background: "background",
  approvedProductCutout: "approved-product-cutout",
  icon: "icon",
  mask: "mask",
  font: "font",
} as const

export const GALLERY_CANVAS_COPY_FIELD = {
  headline: "headline",
  subheadline: "subheadline",
  benefit: "benefit",
  trust: "trust",
} as const

const finiteNumber = z.number().finite()
const normalized = finiteNumber.min(0).max(1)
const layerIdSchema = z.string().regex(/^[a-z][a-z0-9-]{0,63}$/)
const assetIdSchema = z.string().regex(/^[a-f0-9]{64}$/)
const colorSchema = z.string().regex(/^#[a-fA-F0-9]{6}([a-fA-F0-9]{2})?$/)
const fontFamilySchema = z.string().regex(/^[A-Za-z][A-Za-z0-9 -]{0,79}$/)

export const galleryCanvasBoundsSchema = z
  .object({
    x: normalized,
    y: normalized,
    width: normalized.positive(),
    height: normalized.positive(),
  })
  .strict()
  .superRefine((bounds, context) => {
    if (bounds.x + bounds.width > 1 || bounds.y + bounds.height > 1) {
      context.addIssue({
        code: "custom",
        message: "bounds must stay inside normalized canvas coordinates",
      })
    }
  })

export type GalleryCanvasBounds = z.infer<typeof galleryCanvasBoundsSchema>

const commonLayerShape = {
  id: layerIdSchema,
  bounds: galleryCanvasBoundsSchema,
  rotation: finiteNumber.min(-360).max(360),
  zIndex: z.number().int().min(-10_000).max(10_000),
  opacity: normalized,
  visible: z.boolean(),
  locked: z.boolean(),
  collisionPolicy: z.enum(["avoid", "allow", "obstacle"]),
}

const backgroundLayerSchema = z
  .object({
    ...commonLayerShape,
    kind: z.literal(GALLERY_CANVAS_LAYER_KIND.background),
    assetId: assetIdSchema,
    fit: z.enum(["cover", "contain"]),
    focalPoint: z.object({ x: normalized, y: normalized }).strict(),
  })
  .strict()

const productLayerSchema = z
  .object({
    ...commonLayerShape,
    kind: z.literal(GALLERY_CANVAS_LAYER_KIND.product),
    collisionPolicy: z.literal("obstacle"),
    assetId: assetIdSchema,
    approvedCutout: z.literal(true),
    maskAssetId: assetIdSchema.optional(),
    edgeCleanup: z.object({ inset: normalized.max(0.04), feather: normalized.max(0.04) }).strict(),
    contactShadow: z
      .object({
        color: colorSchema,
        opacity: normalized,
        blur: normalized.max(0.1),
        offsetX: finiteNumber.min(-0.2).max(0.2),
        offsetY: finiteNumber.min(-0.2).max(0.2),
        scaleX: normalized.min(0.1),
      })
      .strict(),
    colorMatch: z.object({ ambientColor: colorSchema, strength: normalized }).strict(),
  })
  .strict()

const textSourceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("generic"), text: z.string().min(1).max(2_000) }).strict(),
  z
    .object({
      kind: z.literal("structured"),
      field: z.enum([
        GALLERY_CANVAS_COPY_FIELD.headline,
        GALLERY_CANVAS_COPY_FIELD.subheadline,
        GALLERY_CANVAS_COPY_FIELD.benefit,
        GALLERY_CANVAS_COPY_FIELD.trust,
      ]),
    })
    .strict(),
])

const textLayerSchema = z
  .object({
    ...commonLayerShape,
    kind: z.literal(GALLERY_CANVAS_LAYER_KIND.text),
    source: textSourceSchema,
    style: z
      .object({
        fontAssetId: assetIdSchema,
        fontFallbackAssetIds: z.array(assetIdSchema).max(7).optional(),
        fontFamily: fontFamilySchema,
        fontSize: normalized.positive().max(0.3),
        fontWeight: z.number().int().min(100).max(900),
        lineHeight: finiteNumber.min(0.8).max(3),
        color: colorSchema,
        align: z.enum(["left", "center", "right"]),
        wrap: z.enum(["word", "balance", "none"]),
        letterSpacing: finiteNumber.min(-0.02).max(0.1),
      })
      .strict(),
  })
  .strict()

const iconLayerSchema = z
  .object({
    ...commonLayerShape,
    kind: z.literal(GALLERY_CANVAS_LAYER_KIND.icon),
    assetId: assetIdSchema,
    tint: colorSchema.optional(),
  })
  .strict()

const shapeLayerSchema = z
  .object({
    ...commonLayerShape,
    kind: z.literal(GALLERY_CANVAS_LAYER_KIND.shape),
    shape: z.enum(["rectangle", "ellipse", "line"]),
    fill: colorSchema,
    stroke: z
      .object({ color: colorSchema, width: normalized.max(0.04) })
      .strict()
      .optional(),
    radius: normalized.max(0.5),
  })
  .strict()

const panelLayerSchema = z
  .object({
    ...commonLayerShape,
    kind: z.literal(GALLERY_CANVAS_LAYER_KIND.panel),
    fill: colorSchema,
    border: z
      .object({ color: colorSchema, width: normalized.max(0.04) })
      .strict()
      .optional(),
    radius: normalized.max(0.5),
    blur: normalized.max(0.1),
  })
  .strict()

const groupLayerSchema = z
  .object({
    ...commonLayerShape,
    kind: z.literal(GALLERY_CANVAS_LAYER_KIND.group),
    children: z.array(layerIdSchema).min(1).max(MAX_GALLERY_CANVAS_LAYERS),
  })
  .strict()

export const galleryCanvasLayerSchema = z.discriminatedUnion("kind", [
  backgroundLayerSchema,
  productLayerSchema,
  textLayerSchema,
  iconLayerSchema,
  shapeLayerSchema,
  panelLayerSchema,
  groupLayerSchema,
])

export type GalleryCanvasLayer = z.infer<typeof galleryCanvasLayerSchema>
export type GalleryCanvasLayerKind = GalleryCanvasLayer["kind"]
export type GalleryCanvasTextLayer = Extract<GalleryCanvasLayer, { kind: "text" }>

export const galleryCanvasOutputSchema = z
  .object({
    width: z.number().int().min(320).max(8_192),
    height: z.number().int().min(320).max(8_192),
    format: z.enum(["png", "jpeg"]),
    quality: z.number().int().min(1).max(100),
    backingScale: z.number().int().min(1).max(4),
    safeBounds: galleryCanvasBoundsSchema,
  })
  .strict()
  .superRefine((output, context) => {
    if (output.width * output.height > 40_000_000) {
      context.addIssue({ code: "custom", message: "output exceeds the 40 megapixel limit" })
    }
    if (output.width % output.backingScale !== 0 || output.height % output.backingScale !== 0) {
      context.addIssue({
        code: "custom",
        message: "output dimensions must be divisible by backingScale",
      })
    }
  })

export type GalleryCanvasOutput = z.infer<typeof galleryCanvasOutputSchema>

const copySchema = z
  .object({
    headline: z.string().min(1).max(240).optional(),
    subheadline: z.string().min(1).max(500).optional(),
    benefit: z.string().min(1).max(500).optional(),
    trust: z.string().min(1).max(500).optional(),
  })
  .strict()

export const galleryCanvasDocumentSchema = z
  .object({
    version: z.literal(GALLERY_CANVAS_VERSION),
    id: z.string().regex(/^[a-z][a-z0-9-]{0,79}$/),
    output: galleryCanvasOutputSchema,
    copy: copySchema,
    layers: z.array(galleryCanvasLayerSchema).min(1).max(MAX_GALLERY_CANVAS_LAYERS),
  })
  .strict()

export type GalleryCanvasDocument = z.infer<typeof galleryCanvasDocumentSchema>

export const galleryCanvasAssetSchema = z
  .object({
    id: assetIdSchema,
    role: z.enum([
      GALLERY_CANVAS_ASSET_ROLE.background,
      GALLERY_CANVAS_ASSET_ROLE.approvedProductCutout,
      GALLERY_CANVAS_ASSET_ROLE.icon,
      GALLERY_CANVAS_ASSET_ROLE.mask,
      GALLERY_CANVAS_ASSET_ROLE.font,
    ]),
    mediaType: z.enum(["image/png", "image/jpeg", "image/svg+xml", "font/woff2"]),
    bytesBase64: z
      .string()
      .min(1)
      .max(Math.ceil((MAX_GALLERY_CANVAS_ASSET_BYTES * 4) / 3)),
    fontFamily: fontFamilySchema.optional(),
    fontWeightRange: z
      .object({ min: z.number().int().min(100).max(900), max: z.number().int().min(100).max(900) })
      .strict()
      .optional(),
    unicodeRange: z
      .string()
      .regex(/^(U\+[A-Fa-f0-9?*-]+)(, U\+[A-Fa-f0-9?*-]+)*$/)
      .max(500)
      .optional(),
  })
  .strict()

export type GalleryCanvasAsset = z.infer<typeof galleryCanvasAssetSchema>

export const galleryCanvasRenderRequestSchema = z
  .object({
    document: galleryCanvasDocumentSchema,
    assets: z.array(galleryCanvasAssetSchema).min(1).max(MAX_GALLERY_CANVAS_ASSETS),
  })
  .strict()

export type GalleryCanvasRenderRequest = z.infer<typeof galleryCanvasRenderRequestSchema>

export type GalleryCanvasValidationCode =
  | "asset_hash_mismatch"
  | "asset_bytes_invalid"
  | "asset_encoding_invalid"
  | "asset_media_role_mismatch"
  | "cyclic_group"
  | "document_too_large"
  | "duplicate_asset_id"
  | "duplicate_layer_id"
  | "group_child_missing"
  | "group_child_reused"
  | "missing_asset"
  | "missing_structured_copy"
  | "product_cutout_count"
  | "total_assets_too_large"
  | "unsupported_glyph"
  | "unsupported_font"

export type GalleryCanvasValidationIssue = {
  code: GalleryCanvasValidationCode
  subject: string
  message: string
}
